/**
 * ModerationDashboardPage Component
 *
 * Dashboard for gym moderators to review and manage flagged content.
 * Displays pending flags with filtering and allows moderators to take action.
 *
 * Access requires: canReviewFlags permission (owner, manager, shift_lead, or explicit permission)
 */

import { useState, type CSSProperties } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '@/hooks/useAuth';
import { useGymPermissions } from '@/hooks/useGymPermissions';
import { useContentFlags } from '@/hooks/useContentFlags';
import { FlagCard } from '@/components/moderation/FlagCard';
import { FlagActionModal } from '@/components/moderation/FlagActionModal';
import type { ContentFlag, FlagStatus, FlagAction } from '@flash/shared';

/**
 * Filter tab type including 'all' option
 */
type FilterTab = FlagStatus | 'all';

/**
 * Tab configuration
 */
interface TabConfig {
  key: FilterTab;
  label: string;
  icon?: React.ReactNode;
}

const TABS: TabConfig[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending', icon: <FlagIcon fontSize="small" /> },
  { key: 'reviewed', label: 'Reviewed', icon: <VisibilityIcon fontSize="small" /> },
  { key: 'actioned', label: 'Actioned', icon: <CheckCircleIcon fontSize="small" /> },
  { key: 'dismissed', label: 'Dismissed', icon: <CancelIcon fontSize="small" /> },
];

const VALID_TABS: FilterTab[] = ['all', 'pending', 'reviewed', 'actioned', 'dismissed'];

/**
 * ModerationDashboardPage displays flagged content for moderator review
 */
export const ModerationDashboardPage = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const { user, loading: authLoading } = useAuth();
  const {
    canReviewFlags,
    loading: permissionsLoading,
    isBanned,
    gymRole,
  } = useGymPermissions(gymId);

  // Tab state
  const [activeTab, setActiveTab] = useState<FilterTab>('pending');

  // Modal state
  const [selectedFlag, setSelectedFlag] = useState<ContentFlag | null>(null);
  const [modalAction, setModalAction] = useState<'resolve' | 'dismiss' | null>(null);

  // Fetch flags
  const {
    flags,
    loading: flagsLoading,
    error,
    statusCounts,
    resolveFlag,
    dismissFlag,
    refetch,
  } = useContentFlags({
    gymId: gymId ?? '',
    status: activeTab === 'all' ? 'all' : activeTab,
    moderatorRole: gymRole ?? undefined,
  });

  const loading = authLoading || permissionsLoading;

  // Handle tab change
  const handleTabChange = (tab: FilterTab) => {
    if (VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    }
  };

  // Get count for a specific tab
  const getTabCount = (tab: FilterTab): number => {
    if (tab === 'all') {
      return statusCounts.total;
    }
    // Type assertion since we know tab is a valid FlagStatus key
    return statusCounts[tab as FlagStatus];
  };

  // Open action modal
  const handleOpenModal = (flag: ContentFlag, action: 'resolve' | 'dismiss') => {
    setSelectedFlag(flag);
    setModalAction(action);
  };

  // Close action modal
  const handleCloseModal = () => {
    setSelectedFlag(null);
    setModalAction(null);
  };

  // Handle flag action
  const handleFlagAction = async (action: FlagAction, notes?: string) => {
    if (!selectedFlag) return;

    try {
      if (modalAction === 'resolve') {
        await resolveFlag(selectedFlag.id, action, notes);
      } else if (modalAction === 'dismiss') {
        await dismissFlag(selectedFlag.id, notes);
      }
      handleCloseModal();
    } catch (err) {
      console.error('Error processing flag:', err);
      throw err;
    }
  };

  // Loading state
  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading moderation dashboard...</p>
        </div>
      </main>
    );
  }

  // Auth check
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Permission check
  if (!canReviewFlags || isBanned) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={accessDeniedStyle}>
            <h2 style={accessDeniedTitleStyle}>Access Denied</h2>
            <p style={accessDeniedTextStyle}>
              You don&apos;t have permission to access the moderation dashboard.
            </p>
            <Link to={`/gyms/${gymId}`} style={backLinkStyle}>
              Return to Gym
            </Link>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error Loading Flags</h2>
            <p style={errorTextStyle}>{error}</p>
            <button onClick={() => refetch()} style={retryButtonStyle}>
              Try Again
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <Link to={`/gyms/${gymId}/settings`} style={backButtonStyle}>
            <ArrowBackIcon fontSize="small" />
            <span>Back to Settings</span>
          </Link>
          <div style={headerMainStyle}>
            <div>
              <h1 style={titleStyle}>Content Moderation</h1>
              <p style={subtitleStyle}>Review flagged content and take appropriate action</p>
            </div>
          </div>
        </header>

        {/* Filter Tabs */}
        <div style={tabsContainerStyle}>
          <div style={tabsStyle}>
            {TABS.map((tab) => {
              const count = getTabCount(tab.key);
              const isActive = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    ...tabStyle,
                    ...(isActive ? activeTabStyle : {}),
                  }}
                >
                  {tab.icon && <span style={tabIconStyle}>{tab.icon}</span>}
                  <span>{tab.label}</span>
                  <span
                    style={{
                      ...tabCountStyle,
                      ...(isActive ? activeTabCountStyle : {}),
                      ...(tab.key === 'pending' && count > 0 ? pendingCountStyle : {}),
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Empty State */}
        {!flagsLoading && flags.length === 0 && (
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>
              <FlagIcon style={{ fontSize: 48, color: 'var(--color-text-muted)' }} />
            </div>
            <h2 style={emptyTitleStyle}>No flags found</h2>
            <p style={emptyDescriptionStyle}>
              {activeTab === 'all'
                ? 'No content has been flagged yet.'
                : activeTab === 'pending'
                  ? 'Great job! No pending flags to review.'
                  : `No ${activeTab.replace('_', ' ')} flags.`}
            </p>
          </div>
        )}

        {/* Loading Flags */}
        {flagsLoading && (
          <div style={loadingFlagsStyle}>
            <p style={loadingTextStyle}>Loading flags...</p>
          </div>
        )}

        {/* Flags List */}
        {!flagsLoading && flags.length > 0 && (
          <div style={flagsListStyle}>
            {flags.map((flag) => (
              <FlagCard
                key={flag.id}
                flag={flag}
                onResolve={() => handleOpenModal(flag, 'resolve')}
                onDismiss={() => handleOpenModal(flag, 'dismiss')}
              />
            ))}
          </div>
        )}

        {/* Summary */}
        {flags.length > 0 && (
          <div style={summaryStyle}>
            <p style={summaryTextStyle}>
              Showing {flags.length} of {statusCounts.total} total flags
              {statusCounts.pending > 0 && (
                <span style={pendingSummaryStyle}> ({statusCounts.pending} pending)</span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {selectedFlag && modalAction && (
        <FlagActionModal
          isOpen={true}
          flag={selectedFlag}
          action={modalAction}
          onClose={handleCloseModal}
          onSubmit={handleFlagAction}
        />
      )}
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
};

const contentStyle: CSSProperties = {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
  padding: '40px',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
};

const loadingFlagsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: '40px',
};

const accessDeniedStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '60px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const accessDeniedTitleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const accessDeniedTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '24px',
};

const backLinkStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: 500,
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const retryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const backButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 0',
  color: 'var(--color-text-secondary)',
  textDecoration: 'none',
  fontSize: '0.875rem',
  marginBottom: '12px',
};

const headerMainStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '16px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 4px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const tabsContainerStyle: CSSProperties = {
  marginBottom: '24px',
  overflowX: 'auto',
};

const tabsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  backgroundColor: 'var(--color-bg-primary)',
  padding: '8px',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const tabStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 16px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  transition: 'all 0.2s',
  whiteSpace: 'nowrap',
};

const activeTabStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary)',
  color: 'white',
};

const tabIconStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const tabCountStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '20px',
  height: '20px',
  padding: '0 6px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-secondary)',
  borderRadius: '9999px',
};

const activeTabCountStyle: CSSProperties = {
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
};

const pendingCountStyle: CSSProperties = {
  backgroundColor: 'var(--color-error)',
  color: 'white',
};

const emptyStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '60px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const emptyIconStyle: CSSProperties = {
  marginBottom: '20px',
};

const emptyTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  margin: '0 0 8px 0',
};

const emptyDescriptionStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  maxWidth: '400px',
  lineHeight: 1.6,
  margin: 0,
};

const flagsListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginBottom: '24px',
};

const summaryStyle: CSSProperties = {
  textAlign: 'center',
};

const summaryTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-muted)',
  margin: 0,
};

const pendingSummaryStyle: CSSProperties = {
  color: 'var(--color-error)',
  fontWeight: 500,
};

export default ModerationDashboardPage;
